import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { Paper, FormControlLabel, Checkbox } from "@material-ui/core";
import SimpleDialogDemo from "./SimpleDialog";
import TraduccionesSeccionTableCopy from "./TraduccionesSeccionTableCopy";
import { updateOrganizerWebAttribute } from "../../../data-store/actions/organizers-actions";

const IDIOMAS_DISPONIBLES = [
  { value: "en", label: "Inglés" },
  { value: "fr", label: "Francés" },
  { value: "it", label: "Italiano" },
  { value: "de", label: "Alemán" },
  { value: "pt", label: "Portugués" },
];

const POSICION_POR_DEFECTO = "top-right";
const POSICIONES_BOTON = [
  { value: "top-left", label: "Arriba izquierda" },
  { value: "top-right", label: "Arriba derecha" },
  { value: "bottom-left", label: "Abajo izquierda" },
  { value: "bottom-right", label: "Abajo derecha" },
];

const TAMANO_POR_DEFECTO = "medium";
const TAMANOS_BOTON = [
  { value: "small", label: "Pequeño" },
  { value: "medium", label: "Mediano" },
  { value: "large", label: "Grande" },
];

const MARGEN_POR_DEFECTO = 12;
const MARGEN_MINIMO = 0;
const MARGEN_MAXIMO = 60;
const MARGEN_PASO = 4;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "30px",
    textAlign: "left",
    minHeight: "300px",
    overflowY: "scroll",
  },
}));

// Equivalente a UpdateIdiomas.jsx pero para la web propia del organizador
// (varias vistas/"containers" en vez de una sola web de evento). La lista de
// idiomas habilitados y la apariencia del botón son compartidas por toda la
// web del organizador (se guardan en la raíz, web/idiomas/); la tabla de
// traducción de contenido sí es específica de la vista que se está editando
// (pageViews), para traducir cada vista por separado.
export default function UpdateIdiomasCopy({ businessId, domain, container, pageViews }) {
  const dispatch = useDispatch();
  const organizerWeb = useSelector((state) => state.organizer.organizerWeb);
  const classes = useStyles();

  const seleccionados =
    (organizerWeb && organizerWeb.idiomas && organizerWeb.idiomas.seleccionados) || [];
  const posicionBoton =
    (organizerWeb && organizerWeb.idiomas && organizerWeb.idiomas.posicionBoton) ||
    POSICION_POR_DEFECTO;
  const tamanoBoton =
    (organizerWeb && organizerWeb.idiomas && organizerWeb.idiomas.tamanoBoton) ||
    TAMANO_POR_DEFECTO;
  const margenBoton =
    organizerWeb && organizerWeb.idiomas && organizerWeb.idiomas.margenBoton !== undefined
      ? organizerWeb.idiomas.margenBoton
      : MARGEN_POR_DEFECTO;

  function toggleIdioma(value) {
    const siguientes = seleccionados.includes(value)
      ? seleccionados.filter((idioma) => idioma !== value)
      : [...seleccionados, value];

    dispatch(
      updateOrganizerWebAttribute(businessId, domain, "idiomas/", {
        seleccionados: siguientes,
      })
    );
  }

  function elegirPosicionBoton(value) {
    dispatch(
      updateOrganizerWebAttribute(businessId, domain, "idiomas/", {
        posicionBoton: value,
      })
    );
  }

  function elegirTamanoBoton(value) {
    dispatch(
      updateOrganizerWebAttribute(businessId, domain, "idiomas/", {
        tamanoBoton: value,
      })
    );
  }

  function cambiarMargenBoton(delta) {
    const siguiente = Math.min(
      MARGEN_MAXIMO,
      Math.max(MARGEN_MINIMO, margenBoton + delta)
    );
    dispatch(
      updateOrganizerWebAttribute(businessId, domain, "idiomas/", {
        margenBoton: siguiente,
      })
    );
  }

  const organizerView = (
    <Paper className={classes.paper}>
      <Grid container direction="column" alignItems="center">
        <Grid item xs={12}>
          <Typography gutterBottom variant={"h4"} component="h2">
            Idiomas de la web
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} style={{ margin: "20px" }}>
          <Typography gutterBottom variant={"subtitle1"}>
            Además del español (idioma base), selecciona los idiomas
            adicionales que quieres ofrecer en tu web. Esta selección aplica a
            toda tu web, no solo a esta vista.
          </Typography>
          <Grid container direction="column">
            {IDIOMAS_DISPONIBLES.map((idioma) => (
              <FormControlLabel
                key={idioma.value}
                control={
                  <Checkbox
                    checked={seleccionados.includes(idioma.value)}
                    onChange={() => toggleIdioma(idioma.value)}
                    color="primary"
                  />
                }
                label={idioma.label}
              />
            ))}
          </Grid>
        </Grid>

        {seleccionados.length > 0 && (
          <Grid item xs={12} sm={12} style={{ margin: "20px" }}>
            <Typography gutterBottom variant={"h5"} component="h3">
              Posición del botón de idiomas
            </Typography>
            <Typography gutterBottom variant={"body2"} color="textSecondary">
              Elige en qué esquina de la pantalla se muestra el botón para
              que el visitante cambie de idioma.
            </Typography>
            <Grid
              container
              style={{ maxWidth: 360, margin: "0 auto" }}
              spacing={2}
            >
              {POSICIONES_BOTON.map((posicion) => (
                <Grid item xs={6} key={posicion.value}>
                  <Button
                    fullWidth
                    variant={
                      posicionBoton === posicion.value
                        ? "contained"
                        : "outlined"
                    }
                    color="primary"
                    onClick={() => elegirPosicionBoton(posicion.value)}
                  >
                    {posicion.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        {seleccionados.length > 0 && (
          <Grid item xs={12} sm={12} style={{ margin: "20px" }}>
            <Typography gutterBottom variant={"h5"} component="h3">
              Tamaño del botón de idiomas
            </Typography>
            <Grid
              container
              style={{ maxWidth: 360, margin: "0 auto" }}
              spacing={2}
            >
              {TAMANOS_BOTON.map((tamano) => (
                <Grid item xs={4} key={tamano.value}>
                  <Button
                    fullWidth
                    variant={
                      tamanoBoton === tamano.value ? "contained" : "outlined"
                    }
                    color="primary"
                    onClick={() => elegirTamanoBoton(tamano.value)}
                  >
                    {tamano.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        {seleccionados.length > 0 && (
          <Grid item xs={12} sm={12} style={{ margin: "20px" }}>
            <Typography gutterBottom variant={"h5"} component="h3">
              Margen respecto a la esquina
            </Typography>
            <Typography gutterBottom variant={"body2"} color="textSecondary">
              Controla qué tan separado queda el botón del borde de la
              pantalla.
            </Typography>
            <Grid
              container
              alignItems="center"
              justify="center"
              spacing={1}
            >
              <Grid item>
                <IconButton
                  color="primary"
                  disabled={margenBoton <= MARGEN_MINIMO}
                  onClick={() => cambiarMargenBoton(-MARGEN_PASO)}
                >
                  <RemoveIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <Typography variant="h6" style={{ minWidth: 60, textAlign: "center" }}>
                  {margenBoton}px
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  color="primary"
                  disabled={margenBoton >= MARGEN_MAXIMO}
                  onClick={() => cambiarMargenBoton(MARGEN_PASO)}
                >
                  <AddIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}

        {seleccionados.length > 0 && (
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <Typography gutterBottom variant={"body2"} color="textSecondary">
              Las traducciones de abajo son solo de esta vista ({container}).
              Abre cada vista para traducir su propio contenido.
            </Typography>
            <TraduccionesSeccionTableCopy
              idiomas={seleccionados}
              businessId={businessId}
              domain={domain}
              container={container}
              views={pageViews}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  return (
    <Grid container direction="column" alignItems="center">
      <Grid item xs={12}>
        <SimpleDialogDemo buttonText={"Idiomas"}>{organizerView}</SimpleDialogDemo>
      </Grid>
    </Grid>
  );
}
